import {
    COLLECTION_TYPE_SYMBOL,
    type Collection,
    type CollectionEntry,
    type CollectionIterateFn,
    isCollection,
} from '../collection';
import { freezeCollection, hashCollection } from '../hash';

export type ImmutableSetInit<T> = Collection<any, T> | Set<T> | Array<T> | undefined;

export const SET_TYPE_SYMBOL = Symbol('set');
const NOSET = Symbol('no set');

type SetHashMap<V> = Map<number, Set<V & Collection<any, any>>>;

let emptySet: undefined | ImmutableSet<any>;

/**
 * A Collection of unique values.
 * Compared to a native Set, this collection is immutable and also enforce unique collection entries.
 */
export class ImmutableSet<Value> implements Collection<number, Value> {
    public static isKeyed = false;
    public static isOrdered = false;
    public [COLLECTION_TYPE_SYMBOL] = SET_TYPE_SYMBOL;
    public readonly size: number;

    protected readonly _values: Set<Value>;
    protected readonly _hashMap: SetHashMap<Value>;

    constructor(
        values: Set<Value>,
        hashMap: SetHashMap<Value>,
        constructorTypeCheck: typeof SET_TYPE_SYMBOL
    ) {
        if (constructorTypeCheck !== SET_TYPE_SYMBOL) {
            throw new Error(
                'ImmutableSet constructor is private, use ImmutableSet.create() instead'
            );
        }

        this._hashMap = hashMap;
        this._values = values;
        this.size = values.size;
    }

    /**
     * Create a set from an array of values.
     * This function is optimized to return the same instance when initializing with an empty array.
     */
    public static create<T>(values?: ImmutableSetInit<T>): ImmutableSet<T> {
        if (ImmutableSet.isSet(values)) {
            return values;
        }

        const [set, hashMap] = initializeNativeSet(values);

        if (set.size === 0) {
            emptySet =
                emptySet || freezeCollection(new ImmutableSet(set, hashMap, SET_TYPE_SYMBOL));
            return emptySet;
        }

        return freezeCollection(new ImmutableSet(set, hashMap, SET_TYPE_SYMBOL));
    }

    public static isSet(maybeSet: any): maybeSet is ImmutableSet<any> {
        return isCollection(maybeSet, SET_TYPE_SYMBOL);
    }

    public getByIndex(index: number): CollectionEntry<number, Value> | undefined {
        if (index < 0 || index >= this._values.size) {
            return undefined;
        }

        // @ts-ignore
        return [index, this._values[index]];
    }

    public getByKey(key: number): CollectionEntry<number, Value> | undefined {
        return this.getByIndex(key);
    }

    /**
     * Returns true if this Set includes no values.
     */
    public isEmpty(): boolean {
        return this._values.size === 0;
    }

    /**
     * Replace the entire list by an array of values.
     */
    public update(values: Value[] | Set<Value>): this {
        const [set, hashMap] = initializeNativeSet(values);
        return freezeCollection(makeSet(this, set, hashMap));
    }

    public get(index: number): Value {
        // @ts-ignore
        return this._values[index];
    }

    /**
     * True if a key exists within this Set.
     * This function is optimized to use unique collections even when hashes collided and the references are different.
     */
    public has(value: Value): boolean {
        const has = loopkupRefInHashMap(this._values, this._hashMap, value);
        return has !== NOSET;
    }

    /**
     * Resolve a value to its reference in this Set.
     */
    public deref(value: Value): Value | undefined {
        const ref = loopkupRefInHashMap(this._values, this._hashMap, value);
        return ref === NOSET ? undefined : ref;
    }

    /**
     * Compare the value of this Set to the value of another Set.
     */
    public equals(other: any): boolean {
        if (this === other) {
            return true;
        }

        if (this.constructor !== other.constructor || this.size !== other.size) {
            return false;
        }

        if (hashCollection(this) !== hashCollection(other)) {
            return false;
        }

        return this.every((value) => other.has(value));
    }

    /**
     * Returns the first value for which the predicate returns true.
     */
    public find(callback: (value: Value, index: number, set: this) => boolean): Value | undefined {
        let index = 0;
        for (const value of this._values) {
            if (callback(value, index, this)) {
                return value;
            }

            index++;
        }
    }

    /**
     * Returns a new Set which also includes this value.
     */
    public add(value: Value): this {
        if (this.has(value)) {
            return this;
        }

        return this.withHashedMutations((set, hashMap) => {
            set.add(value);
            indexValueInHashMap(hashMap, value);
        });
    }

    /**
     * Returns a new Set which excludes this value.
     */
    public delete(value: Value): this {
        const existing = loopkupRefInHashMap(this._values, this._hashMap, value);
        if (existing === NOSET) {
            return this;
        }

        return this.withHashedMutations((set, hashMap) => {
            set.delete(existing);
            unindexValueInHashMap(hashMap, existing);
        });
    }

    /**
     * Returns a new Set containing no values.
     */
    public clear(): this {
        return this.update([]);
    }

    /**
     * Returns a Set including any value from collections that does not already exist in this Set.
     */
    public union(other: this): this {
        return this.withHashedMutations((set, hashMap) => {
            let modified = false;

            other._values.forEach((value) => {
                if (!this.has(value)) {
                    modified = true;
                    set.add(value);
                    indexValueInHashMap(hashMap, value);
                }
            });

            return modified;
        });
    }

    /**
     * Return an intersection between this Set and another Set.
     */
    public intersect(other: this): this {
        return this.withHashedMutations((set, hashMap) => {
            let modified = false;

            set.forEach((value) => {
                if (!other.has(value)) {
                    modified = true;
                    set.delete(value);
                    unindexValueInHashMap(hashMap, value);
                }
            });

            return modified;
        });
    }

    /**
     * Return a new set with only the values for which the predicate returns true.
     */
    public filter(callback: (value: Value, index: number, set: this) => boolean): this {
        const set = new Set<Value>();
        const hashMap: SetHashMap<Value> = new Map();

        this.forEach((value, index) => {
            if (callback(value, index, this)) {
                set.add(value);
                indexValueInHashMap(hashMap, value);
            }
        });

        // @ts-ignore
        return freezeCollection(makeSet(this, set, hashMap));
    }

    /**
     * Converts this Set collection to equivalent native JavaScript Set.
     */
    public toSet(): Set<Value> {
        return new Set(this._values);
    }

    /**
     * Converts this Set collection to equivalent native JavaScript Array.
     */
    public toArray(): Value[] {
        return Array.from(this._values);
    }

    /**
     * The `callback` is executed for every entry in the Set.
     * Unlike Array#forEach, if any call of sideEffect returns false, the iteration will stop.
     * Returns the number of entries iterated (including the last iteration which returned false).
     */
    public forEach(
        callback: (value: Value, index: number, array: ImmutableSet<Value>) => any
    ): number {
        let iterations = 0;

        for (const value of this._values) {
            if (callback(value, iterations, this) === false) {
                break;
            }
            iterations++;
        }

        return iterations;
    }

    /**
     * True if predicate returns true for all entries in the Set.
     */
    public every(callback: (value: Value, index: number, set: this) => boolean): boolean {
        let index = 0;
        for (const value of this._values) {
            if (!callback(value, index, this)) {
                return false;
            }
            index++;
        }

        return true;
    }

    /**
     * True if predicate returns true for any entry in the Collection.
     */
    public some(callback: (value: Value, index: number, set: this) => boolean): boolean {
        return this.toArray().some((value, index) => callback(value, index, this));
    }

    /**
     * Executes a user-supplied "reducer" callback function on each element of the collection, in order, passing in the return value from the calculation on the preceding element.
     * The final result of running the reducer across all elements of the collection is a single value.
     */
    public reduce<U>(
        callback: (previousValue: U, currentValue: Value, currentIndex: number, map: this) => U,
        initialValue: U
    ): U {
        return this.toArray().reduce(
            (previousValue, currentValue, currentIndex) =>
                callback(previousValue, currentValue, currentIndex, this),
            initialValue
        );
    }

    /**
     * Returns a new Set with values passed through a mapper function.
     */
    public map<Mapped>(
        callback: (value: Value, index: number, set: this) => Mapped
    ): ImmutableSet<Mapped> {
        const set = new Set<Mapped>();
        const hashMap: SetHashMap<Mapped> = new Map();

        this.forEach((value, index) => {
            const mapped = callback(value, index, this);
            set.add(mapped);
            indexValueInHashMap(hashMap, mapped);
        });

        // @ts-ignore
        return freezeCollection(makeSet(this, set, hashMap));
    }

    /**
     * Execute a callback on a native set. This method is efficient to performant multiple operations
     * on a native mutable set.
     *
     * Since the native set only holds references to the values, the callback is responsible for
     * looking the right value for a collection using the `deref` method.
     *
     * Returning false, cancels the operation.
     */
    public withMutations(callback: (set: Set<Value>) => void | boolean): this {
        const set = this.toSet();
        if (callback(set) === false) {
            return this;
        }
        return this.update(set);
    }

    private withHashedMutations(
        callback: (set: Set<Value>, hashMap: SetHashMap<Value>) => void | boolean
    ): this {
        const set = this.toSet();
        const hashMap = deepCopyHashMap(this._hashMap);
        if (callback(set, hashMap) === false) {
            return this;
        }
        return makeSet(this, set, hashMap);
    }

    /**
     * Super-set of the collection iterate.
     */
    public iterate(fn: CollectionIterateFn<number, Value>): void {
        this.forEach((value, index) => fn(value, index));
    }

    public toKeysArray(): number[] {
        return [...this.toArray().keys()];
    }

    public toValuesArray(): Value[] {
        return this.toArray();
    }

    public toEntriesArray(): [number, Value][] {
        return this.toArray().map((value, index) => [index, value]);
    }

    public hashCode(): number {
        return hashCollection(this);
    }
}

/**
 * For fast type-checking.
 * We store it on the prototype so that it's not enumerable and will be copied over by makeSet.
 */
ImmutableSet.prototype[COLLECTION_TYPE_SYMBOL] = SET_TYPE_SYMBOL;

/**
 * Initialize the internal values of an immutable Set based on a given array.
 */
export function initializeNativeSet<T>(values: ImmutableSetInit<T>): [Set<T>, SetHashMap<T>] {
    if (!values) {
        return [new Set(), new Map()];
    }

    if (values instanceof Set) {
        return [values, indexHashMap([...values])];
    }

    if (isCollection(values)) {
        values = values.toValuesArray();
    }

    const set = new Set<T>();
    const hashMap: SetHashMap<T> = new Map();

    for (const value of values) {
        if (set.size > 0) {
            const existing = loopkupRefInHashMap(set, hashMap, value);
            if (existing !== NOSET) {
                continue;
            }
        }

        set.add(value);
        indexValueInHashMap(hashMap, value);
    }

    return [set, hashMap];
}

/**
 * Fast constructor for new list entries as "new X" is slower.
 */
function makeSet<V, T extends ImmutableSet<V>>(
    likeSet: T,
    values: Set<V>,
    hashMap: SetHashMap<V> = indexHashMap([...values.values()])
): T {
    const record = Object.create(Object.getPrototypeOf(likeSet));
    record._values = values;
    record._hashMap = hashMap;
    record.size = values.size;
    return record;
}

/**
 * Index collections values in the hashMap for faster lookup.
 */
function indexHashMap<Value>(values: Value[]): SetHashMap<Value> {
    const hashMap: SetHashMap<Value> = new Map();
    values.forEach((value) => {
        indexValueInHashMap(hashMap, value);
    });

    return hashMap;
}

function indexValueInHashMap(hashMap: SetHashMap<any>, value: any) {
    if (isCollection(value)) {
        const hashCode = hashCollection(value);
        let existing = hashMap.get(hashCode);
        if (!existing) {
            existing = new Set();
            hashMap.set(hashCode, existing);
        }
        existing.add(value);
    }
}

function unindexValueInHashMap(hashMap: SetHashMap<any>, value: any) {
    if (isCollection(value)) {
        const hashCode = hashCollection(value);
        const existing = hashMap.get(hashCode);
        if (existing) {
            existing.delete(value);
            if (existing.size === 0) {
                hashMap.delete(hashCode);
            }
        }
    }
}

function deepCopyHashMap(hashMap: SetHashMap<any>): SetHashMap<any> {
    const newHashMap = new Map();
    hashMap.forEach((values, hashCode) => {
        newHashMap.set(hashCode, new Set(values));
    });

    return newHashMap;
}

/**
 * Low-level implementation for the `has` function which return the actual reference stored in the collection.
 */
function loopkupRefInHashMap<Value>(
    set: Set<Value>,
    hashMap: SetHashMap<Value>,
    value: Value
): Value | typeof NOSET {
    const has = set.has(value);
    if (has) {
        return value;
    }

    if (!isCollection(value)) {
        return NOSET;
    }

    const hashCode = hashCollection(value);
    const values = hashMap.get(hashCode);
    if (!values) {
        return NOSET;
    }

    for (const item of values) {
        if (item.equals(value)) {
            return item;
        }
    }

    return NOSET;
}
